import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "zustand";
import moment from "moment";

// Assets and components
import GoBack from "../../../assets/GoBackApp.svg";
import { ActionBtn } from "../components/action-btn";
import AssetIcon from "../components/asset-icon";
import SavedToFavNotification from "../components/saved-to-favorites-notification";
import SaveToFavBtn from "../components/save-to-favorites-btn";
import HorizontalScroll from "../components/horizontal-scroll";
import { TabBtn } from "../components/tab-btn";
import DepthField from "../components/depth-field";

// Widgets
import { DealsTable, MarketChart } from "widgets/market";
import ExchangeStatusComp from "widgets/market/exchange-status";
import ExchangeSource from "widgets/market/exchange-source";
import ExchangeStatusBtn from "widgets/market/exchange-status-btn";
import FractionalStatusIndicator from "widgets/market/fractional-status-indicator";
import FractionalStatusBottomSheet from "widgets/market/fractional-status-bottom-sheet";
import { AssetWidgetAndBottomSheet } from "../components/asset-widget";

// Hooks and utilities
import { useBlockScrollbarFromIndividuallyWrapped } from "hooks/useBlockScrollbar";
import { useAssetVolatility } from "hooks/useAssetVolatility";
import useExchangeTimeFrame from "hooks/useExchangeTimeFrame";
import useCanAction from "hooks/useCanAction";
import useSaveHandler from "hooks/useSaveToFavorites";
import usePrice from "hooks/usePrice";
import useOrderbook from "hooks/useOrderbook";

// Stores and types
import { IDepthItem, marketStore } from "entities/market";
import { dashboardStore } from "entities/dashboard";
import { PATH_LIST } from "shared/lib/react-router";
import { sessionModel } from "entities/session";
import { AssetType, OperationType } from "static/types";
import { GetCountryByCode } from "static/countries";

// Utilities
import { formatDate, formatMarketCap, isHeadUser } from "helpers/functions";
import { formatMoney } from "helpers/money";
import { sircapApi } from "shared/api/sircap";
import OptionChainRow, {
  buildOptionStrikeMap,
  filterStrikes,
} from "../components/option-chain-row";

// Styles
import "./style.scss";
import useSubAccount from "hooks/useSubAccount";
import { fbLogEvent, FB_EVENTS } from "utils/fbEvents";

interface IOptionContract {
  id: number;
  gtn_key: string;
  symbol: string;
  strike: number;
  expiration: string;
  option_type: number;
  lot_size: number;
  decimal_places: number;
  bid: number | null;
  ask: number | null;
  price: number | null;
  open_interest: number | null;
  display_ticker: string | null;
}

interface IOptionChain {
  asset_id: number | null;
  expirations: string[];
  calls: IOptionContract[];
  puts: IOptionContract[];
}

export const StockDetailsPage = () => {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const location = useLocation();
  const { asset_id } = useParams();
  const [activeTab, setActiveTab] = useState("Trading");
  const [stockStatus, setStockStatus] = useState(false);
  const [fractionalModal, setFractionalModal] = useState(false);
  const [toPath, setToPath] = useState<any>(-1);
  const [optionChain, setOptionChain] = useState<IOptionChain | null>(null);
  const [allOptionExpirations, setAllOptionExpirations] = useState<string[]>(
    [],
  );
  const [optionChainLoading, setOptionChainLoading] = useState(false);
  const [optionChainRefreshing, setOptionChainRefreshing] = useState(false);
  const [selectedOptionExp, setSelectedOptionExp] = useState<string>("");
  const [hasOptions, setHasOptions] = useState<boolean | null>(null);
  const [optionAssetId, setOptionAssetId] = useState<number | null>(null);
  const optionFetchedRef = useRef(false);

  const {
    isInitialLoading,
    assetInfo,
    loadAssetInfo,
    loadAssetChart,
    assetDepth,
    sectors,
    stockIndustries,
    addFavorites,
    removeFavorite,
  } = useStore(marketStore);

  const symbol = assetInfo?.asset?.symbol || "";
  const exchange = useExchangeTimeFrame(assetInfo?.asset?.exchange);
  const isExchangeActive = exchange ? !!exchange?.isActive : true;
  const exchangeCode = exchange?.exchange_code;
  const currency = assetInfo?.asset?.currency.symbol;
  const isAMX = assetInfo?.asset?.exchange?.id === 25;
  const isMOEX = exchangeCode === "MOEX";
  const enableOrderbook = (isAMX || isMOEX) && isExchangeActive;
  const availableMarginalTrading =
    assetInfo?.available_marginal_trading || false;

  const { prevPathDeal } = useStore(dashboardStore);

  const action = useCanAction();

  const { isMarginSubAccount } = useSubAccount();
  const canSeeOptions = isMarginSubAccount;

  const { ask, bid, last, mid, change, changePercent } = usePrice({
    exchange: exchangeCode,
    exchangeStatus: exchange?.status,
    symbol,
    assetType: AssetType.Stock,
    ask: assetInfo?.asset?.ask,
    bid: assetInfo?.asset?.bid,
    price: assetInfo?.asset?.price,
    change: assetInfo?.asset?.change,
    useAssetDepth: true,
  });

  const { changedPart, unchangedPart, volatilityElementRef } =
    useAssetVolatility((isAMX ? ask : null) || last || mid);

  const { saved, showNotification, handleSave } = useSaveHandler(
    +asset_id!,
    addFavorites,
    removeFavorite,
  );

  const isCurrency = assetInfo?.asset?.type === AssetType.Currency;
  const { currencies, user_deposit_accounts, user_brokerage_accounts } =
    useStore(dashboardStore);

  useBlockScrollbarFromIndividuallyWrapped(stockStatus, "stock-details", [
    stockStatus,
  ]);

  const handleNavigate = () => {
    navigate(toPath);
  };

  const scroll = () => {
    const stockDetails = document.querySelector(".stock-details");
    if (stockDetails) {
      stockDetails.scrollTop = 0;
    }
  };

  const handleTabClick = (text: string) => {
    setActiveTab(text);
    scroll();
    if (text === "Options" && !optionFetchedRef.current && asset_id) {
      optionFetchedRef.current = true;
      setOptionChainLoading(true);
      sircapApi.market
        .optionChainByBase(+asset_id)({})
        .then((resp) => {
          if (!resp.error && resp.data?.data) {
            const chain = resp.data.data as IOptionChain;
            setOptionChain(chain);
            if (chain.expirations?.length) {
              setAllOptionExpirations((prev) =>
                prev.length ? prev : chain.expirations,
              );
              setSelectedOptionExp((prev) => prev || chain.expirations[0]);
            }
            setOptionAssetId(chain.asset_id ?? null);
          }
        })
        .finally(() => setOptionChainLoading(false));
    }
  };

  const refetchOptionChainExp = useCallback(
    async (expiration: string) => {
      if (!optionAssetId) return;
      setOptionChainRefreshing(true);
      try {
        const resp = await sircapApi.market.optionChain(optionAssetId)({
          params: { expiration },
        });
        if (!resp.error && resp.data?.data) {
          const data = resp.data.data as IOptionChain;
          setOptionChain(data);
          if (data.expirations?.length) {
            setAllOptionExpirations((prev) =>
              prev.length ? prev : data.expirations,
            );
          }
        }
      } finally {
        setOptionChainRefreshing(false);
      }
    },
    [optionAssetId],
  );

  const handleOptionExpChange = (exp: string) => {
    setSelectedOptionExp(exp);
    void refetchOptionChainExp(exp);
  };

  const goToOptionDeal = (
    operation: "buy" | "sell",
    contract: IOptionContract,
    price: number,
  ) => {
    if (!optionAssetId || !contract.id) return;
    const params = new URLSearchParams({
      type: "10",
      price: String(price),
      expiration: contract.expiration,
      strike: String(contract.strike),
      option_type: String(contract.option_type),
    });
    params.set("contract_id", String(contract.id));
    navigate(`${PATH_LIST[operation](optionAssetId)}?${params.toString()}`);
  };

  const optionExpirations = allOptionExpirations.length
    ? allOptionExpirations
    : optionChain?.expirations || [];

  const optionStrikeMap = useMemo(() => {
    if (!optionChain)
      return new Map<
        number,
        { call?: IOptionContract; put?: IOptionContract }
      >();
    return buildOptionStrikeMap(
      optionChain.calls,
      optionChain.puts,
      selectedOptionExp,
    );
  }, [optionChain, selectedOptionExp]);

  const optionStrikes = useMemo(
    () =>
      filterStrikes(Array.from(optionStrikeMap.keys()).sort((a, b) => a - b)),
    [optionStrikeMap],
  );

  useEffect(() => {
    if (!symbol || !asset_id) return;
    void fbLogEvent(FB_EVENTS.contentView);
  }, [asset_id, symbol]);

  const loadOnMount = async () => {
    loadAssetInfo(asset_id);
    loadAssetChart(asset_id);
    if (asset_id) {
      sircapApi.market
        .optionChainByBase(+asset_id)({})
        .then((resp) => {
          if (!resp.error && resp.data?.data) {
            const chain = resp.data.data as IOptionChain;
            const empty = !chain.calls?.length && !chain.puts?.length;
            setHasOptions(!empty);
            if (!empty) {
              optionFetchedRef.current = true;
              setOptionChain(chain);
              setOptionAssetId(chain.asset_id ?? null);
              if (chain.expirations?.length) {
                setAllOptionExpirations((prev) =>
                  prev.length ? prev : chain.expirations,
                );
                setSelectedOptionExp((prev) => prev || chain.expirations[0]);
              }
            }
          } else {
            setHasOptions(false);
          }
        })
        .catch(() => setHasOptions(false));
    }
  };

  useEffect(() => {
    loadOnMount();
  }, []);

  useOrderbook({
    asset_id,
    exchangeCode,
    symbol,
    isAMX,
    enabled: !!enableOrderbook,
  });

  useEffect(() => {
    if (prevPathDeal === true) {
      setToPath(PATH_LIST.market);
    } else {
      setToPath(-1);
    }
  }, [location]);

  const getAssetInfo = useCallback(() => {
    try {
      const info = assetInfo?.info;
      return info;
    } catch (error) {
      return null;
    }
  }, [assetInfo]);

  const getAssetMarketCap = useCallback(() => {
    try {
      const cap = assetInfo?.info?.mktCap;
      const curr = assetInfo?.info?.currency || "";
      if (cap) {
        return `${formatMarketCap(cap)} ${curr}`;
      } else {
        return "";
      }
    } catch (error) {
      return "";
    }
  }, [assetInfo]);

  const getAssetSector = useCallback(() => {
    try {
      const sector = assetInfo?.info?.sector;
      if (sector) {
        const findSector = sectors.find((s) => s.alias === sector);
        return findSector ? findSector[`name_en`] : sector; // ${i18n.language}
      } else {
        return "";
      }
    } catch (error) {
      return "";
    }
  }, [assetInfo, sectors]);

  const getAssetIndustry = useCallback(() => {
    try {
      const industry = assetInfo?.info?.industry;
      if (industry) {
        const findSector = stockIndustries.find((s) => s.alias === industry);
        return findSector ? findSector[`name_en`] : industry; // ${i18n.language}
      } else {
        return "";
      }
    } catch (error) {
      return "";
    }
  }, [assetInfo, stockIndustries]);

  const getAssetCountry = useCallback(() => {
    try {
      const country = assetInfo?.info?.country;
      if (country) {
        return GetCountryByCode(country)?.name || country;
      } else {
        return "";
      }
    } catch (error) {
      return "";
    }
  }, [assetInfo]);

  const getAssetDividends = useCallback(() => {
    try {
      const asset = assetInfo?.asset;
      if (!asset) return [];

      const price = (asset?.bid + asset?.ask) / 2;
      const data = assetInfo?.dividends.historical;
      return data
        ? data
            .filter((d) => !!d.dividend)
            .slice(0, 50)
            .map((d, i) => {
              const currentYear = moment().format("YYYY");
              const year = d.date.split("-")[0];
              const yearDividends = data.filter(
                (f) => f.date.split("-")[0] === year,
              );
              const showYear =
                i === 0 || year !== data[i - 1].date.split("-")[0];

              return {
                ...d,
                showYear,
                percent:
                  year === currentYear
                    ? String(+((100 / price) * d.dividend).toFixed(2)) + "%"
                    : "",
                annuallyYield:
                  showYear && year === currentYear
                    ? String(
                        +(
                          (100 / price) *
                          yearDividends
                            .map((v) => v.dividend)
                            .reduce((a, b) => a + b, 0)
                        ).toFixed(2),
                      ) + "%"
                    : "",
              };
            })
        : [];
    } catch (error) {
      return [];
    }
  }, [assetInfo]);

  const handleDeal = (type: number, operation: string) => {
    if (operation === "buy") {
      navigate(PATH_LIST.buy(asset_id) + `?type=${type}`);
    } else {
      navigate(PATH_LIST.sell(asset_id) + `?type=${type}`);
    }
  };

  const rows = [
    { label: "sector", value: getAssetSector() },
    { label: "Industry", value: getAssetIndustry() },
    { label: "country", value: getAssetCountry() },
    { label: "capitalization", value: getAssetMarketCap() },
  ];

  const infoRows = [
    { label: "ticker", value: getAssetInfo()?.symbol || undefined },
    { label: "ISIN", value: getAssetInfo()?.isin || undefined },
    { label: "currency", value: getAssetInfo()?.currency || undefined },
    { label: "exchange", value: getAssetInfo()?.exchange || undefined },
  ];

  const dividendRow = [
    {
      label: moment(getAssetDividends()[0]?.date).isAfter(
        moment().add(1, "day"),
        "day",
      )
        ? "upcoming"
        : "previous",
      value: formatDate(getAssetDividends()[0]?.date),
    },
    {
      label: "dividendPerShare",
      value: `${+getAssetDividends()[0]?.dividend.toFixed(4)} ${
        assetInfo?.asset?.currency?.symbol || ""
      }`,
      // ·  ·  · ${getAssetDividends()[0]?.percent} ·  ·  ·
    },
  ];

  const assetDividendsInfoRows = getAssetDividends().map((row) => ({
    date: formatDate(row.date),
    amount: +row.dividend.toFixed(4) + " " + assetInfo?.asset?.currency?.symbol,
    // yield: row.percent,
  }));

  const getUserAccount = useCallback(() => {
    if (assetInfo && assetInfo.asset) {
      if (isCurrency) {
        const acc = user_deposit_accounts.find(
          (acc) => acc.currency.code === symbol,
        );
        return {
          symbol: currencies.find((c) => c.code === symbol)?.symbol || "",
          amount: formatMoney(acc?.amount || 0),
          totalAmount: formatMoney(acc?.amount || 0),
        };
      } else {
        const acc = user_brokerage_accounts.find(
          (acc) => acc.asset.symbol === symbol,
        );
        const qty = acc?.quantity || 0;
        return {
          symbol: currencies.find((c) => c.code === symbol)?.symbol || "",
          amount: `${qty} ${symbol}`,
          totalAmount: qty,
        };
      }
    }
    return {
      symbol: currencies.find((c) => c.code === symbol)?.symbol || "",
      amount: 0,
      totalAmount: 0,
    };
  }, [
    user_deposit_accounts,
    user_brokerage_accounts,
    assetInfo,
    currencies,
    isCurrency,
  ]);

  const bestAssetDepth = useMemo(() => {
    if (assetDepth.length === 0) return [];

    const asks = assetDepth.filter((d) => d.operation === OperationType.Buy);
    const bids = assetDepth.filter((d) => d.operation === OperationType.Sell);

    let bestDepth: IDepthItem[] = [];
    if (asks.length > 0) bestDepth.push([...asks].pop()!);
    if (bids.length > 0) bestDepth.push([...bids].shift()!);

    return bestDepth;
  }, [assetDepth]);

  const isEmpty = getUserAccount().totalAmount === 0;

  const infoComponents = [
    <AssetWidgetAndBottomSheet
      key="1"
      title="description"
      content={getAssetInfo()?.description}
      rows={rows}
      infoRows={infoRows}
    />,
    assetInfo?.dividends?.historical?.length ? (
      <AssetWidgetAndBottomSheet
        key="2"
        title="dividends"
        rows={dividendRow}
        dividendRows={assetDividendsInfoRows}
        // bottomSheetContent="В данном разделе отображается информация о рекомендованных или
        //       утвержденных дивидендах. Указанная дата — день, до которого
        //       включительно необходимо купить акции биржевых эмитентов для
        //       получения дивидендов. Начисление дивидендов ориентировочно в
        //       течение 1–2 месяцев "
        bottomSheetContent
      />
    ) : (
      <></>
    ),
  ];

  const showTabs = enableOrderbook || (canSeeOptions && hasOptions === true);

  if (isInitialLoading || !assetInfo) {
    return (
      <div className={`stock-details stock-details${activeTab}`}>
        <div className="stock-details-header">
          <img
            className="go-back-portfolio"
            onClick={handleNavigate}
            src={GoBack}
            alt="Go Back"
          />
          <div>
            <p>{symbol}</p>
          </div>
        </div>
        <div className="stock-details-main">
          <p className="assets-loading assets-loading-page">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="stock-details"
      className={`stock-details stock-details${activeTab}`}
    >
      {showNotification && <SavedToFavNotification symbol={symbol} />}

      <div className="stock-details-header">
        <img
          className="go-back-portfolio"
          onClick={handleNavigate}
          src={GoBack}
          alt="Go Back"
        />
        <div className="stock-details-header-info">
          <AssetIcon
            asset={
              assetInfo?.asset || {
                type: assetInfo?.asset?.type || AssetType.Stock,
                symbol: symbol!,
              }
            }
            className="asset-logo"
          />
          <p>
            {symbol}

            <span>
              ({getAssetInfo()?.companyName || assetInfo?.asset?.name || ""})
            </span>
          </p>
        </div>

        {/* @ts-ignore */}
        <SaveToFavBtn onclick={() => handleSave(asset_id)} saved={saved} />
      </div>

      {showTabs && (
        <HorizontalScroll className="tab-bar">
          <TabBtn
            text={t("trading")}
            active={activeTab === "Trading"}
            onClick={() => handleTabClick("Trading")}
          />
          {enableOrderbook && (
            <TabBtn
              text={t("depth")}
              active={activeTab === "Depth"}
              onClick={() => handleTabClick("Depth")}
            />
          )}
          {canSeeOptions && hasOptions === true && (
            <TabBtn
              text={t("options")}
              active={activeTab === "Options"}
              onClick={() => handleTabClick("Options")}
            />
          )}
        </HorizontalScroll>
      )}

      {activeTab === "Trading" && (
        <div
          className="stock-details-main"
          style={{ marginTop: showTabs ? 0 : "var(--inset-top, 0px)" }}
        >
          <div className="stock-info-bargaining">
            <div className="stock-info">
              <div style={{ width: "100%" }}>
                <div className="stock-info-header">
                  <div className="stock-price-status">
                    {/* //todo ............  */}
                    {/* {changedPart !== NaN && ( */}
                    <p className="price" style={{ display: "flex" }}>
                      {unchangedPart}

                      <span
                        className="price"
                        id="volatility"
                        ref={volatilityElementRef}
                      >
                        {changedPart}
                      </span>
                      {assetInfo?.asset?.currency.symbol}
                    </p>

                    {exchange && (
                      <ExchangeStatusBtn
                        onClick={() => setStockStatus(true)}
                        status={exchange.status}
                      />
                    )}

                    <FractionalStatusIndicator
                      onClick={() => setFractionalModal(true)}
                    />
                  </div>
                  {exchange && (
                    <ExchangeSource
                      name={exchange.exchange_name}
                      code={exchange.exchange_code}
                      image={exchange.exchange_logo}
                    />
                  )}
                </div>
                {change && (
                  <>
                    <div className="gain-percent">
                      {/* <p style={{ marginRight: 12 }}>{t("today")}</p> */}

                      <span className={change < 0 ? "loss" : ""}>
                        {change} {currency}
                      </span>
                      <span
                        className={
                          "gains-percent " + (change < 0 ? "loss" : "")
                        }
                      >
                        {changePercent}%
                      </span>
                    </div>
                  </>
                )}

                {/* <div className="stock-price-status">
                <p
                  className="price"
                  // style={{
                  //   display: "flex",
                  //   flexDirection: "column",
                  //   alignItems: "center",
                  // }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      color: "orange",
                      marginRight: 12,
                    }}
                  >
                    {t("pre-market")}
                  </p>

                  {unchangedPart}

                  <span
                    className="price"
                    id="volatility"
                    ref={volatilityElementRef}
                  >
                    {changedPart}
                  </span>
                  {assetInfo?.asset?.currency.symbol}
                </p>
              </div> */}
              </div>
            </div>
          </div>

          <MarketChart />

          {enableOrderbook && bestAssetDepth.length > 0 && (
            <AssetWidgetAndBottomSheet
              title="depth"
              customClick={() => {
                setActiveTab("Depth");
              }}
              htmlContent={
                <div className="depth-main">
                  <div
                    className="depth-main-header"
                    style={{ position: "static" }}
                  >
                    <p>{t("purchase")}</p>
                    <p>{t("price")}</p>
                    <p>{t("sale")}</p>
                  </div>
                  <div className="depth-content">
                    {bestAssetDepth.map((item, index) => {
                      return (
                        <DepthField
                          key={index}
                          purchase={
                            item.operation === "sell"
                              ? item.quantity
                              : undefined
                          }
                          price={
                            item.price +
                            (assetInfo?.asset?.currency?.symbol || "")
                          }
                          sale={
                            item.operation === "buy" ? item.quantity : undefined
                          }
                          progress={item.progress}
                          currency={""}
                          onClick={() => {
                            navigate(
                              PATH_LIST[item.operation](asset_id) +
                                `?type=${assetInfo!.asset!.type}&price=${
                                  item.price
                                }`,
                            );
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              }
            />
          )}

          <DealsTable
            assetId={assetInfo?.asset?.id || undefined}
            realTimePrices={{ bid, ask }}
          />

          <div className="action-btns-main-wrapper">
            <ActionBtn
              // onlyBtn={isEmpty}
              customClass
              onClick={() =>
                action(() =>
                  handleDeal(assetInfo!.asset!.type, OperationType.Buy),
                )
              }
              action={
                // subAccountSubNumber && availableMarginalTrading
                //   ? t("long")
                //   : t("buy")
                t("buy")
              }
              price={ask}
              currency={currency}
              type="green"
              disabled={!isExchangeActive}
            />
            <ActionBtn
              customClass
              onClick={() =>
                action(() =>
                  handleDeal(assetInfo!.asset!.type, OperationType.Sell),
                )
              }
              action={
                t("sell")
                // subAccountSubNumber && availableMarginalTrading
                //   ? t("short")
                //   : t("sell")
              }
              price={bid}
              currency={currency}
              disabled={!isExchangeActive || isEmpty}
              type="red"
            />
          </div>

          {isEmpty ? infoComponents : infoComponents.reverse()}

          {/* Modals */}
          {fractionalModal && (
            <FractionalStatusBottomSheet
              onClose={() => setFractionalModal(false)}
            />
          )}
          {stockStatus && exchange && (
            <ExchangeStatusComp
              tf={exchange}
              onClose={() => setStockStatus(false)}
            />
          )}
        </div>
      )}

      {activeTab === "Depth" && (
        <div
          className="stock-details-main stock-details-main-review"
          style={{ marginTop: showTabs ? 0 : "var(--inset-top, 0px)" }}
        >
          <div className="depth-header">
            <p>{t("depthOfMarket")}</p>
          </div>

          <div className="depth-main">
            <div className="depth-main-header">
              <p>{t("purchase")}</p>
              <p>{t("price")}</p>
              <p>{t("sale")}</p>
            </div>
            <div className="depth-content">
              {assetDepth.map((item, index) => {
                return (
                  <DepthField
                    key={index}
                    purchase={
                      item.operation === "sell" ? item.quantity : undefined
                    }
                    price={
                      item.price + (assetInfo?.asset?.currency?.symbol || "")
                    }
                    sale={item.operation === "buy" ? item.quantity : undefined}
                    progress={item.progress}
                    currency={""}
                    onClick={() => {
                      navigate(
                        PATH_LIST[item.operation](asset_id) +
                          `?type=${assetInfo!.asset!.type}&price=${item.price}`,
                      );
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {canSeeOptions && activeTab === "Options" && (
        <div
          className="stock-details-main stock-details-main-options"
          style={{ marginTop: showTabs ? 0 : "var(--inset-top, 0px)" }}
        >
          {optionChainLoading ? (
            <p className="stock-options-loading">{t("loading")}</p>
          ) : !optionChain ||
            (!optionChain.calls.length && !optionChain.puts.length) ? (
            <p className="stock-options-empty">{t("notFound")}</p>
          ) : (
            <>
              {optionExpirations.length > 0 && (
                <HorizontalScroll className="stock-options-exp-tabs">
                  {optionExpirations.map((exp) => (
                    <button
                      key={exp}
                      type="button"
                      className={`stock-options-exp-tab ${
                        selectedOptionExp === exp ? "active" : ""
                      }`}
                      onClick={() => handleOptionExpChange(exp)}
                    >
                      {exp}
                    </button>
                  ))}
                </HorizontalScroll>
              )}
              <div className="stock-options-chain-main">
                {optionChainRefreshing ? (
                  <p className="stock-options-loading-inline">{t("loading")}</p>
                ) : optionStrikes.length === 0 ? (
                  <p className="stock-options-empty">{t("notFound")}</p>
                ) : (
                  <div className="stock-options-chain-scroll">
                    <table className="stock-options-chain-table">
                      <thead>
                        <tr>
                          <th colSpan={2} className="call-h">
                            {t("optionsCall")}
                          </th>
                          <th className="strike-h">{t("optionsStrike")}</th>
                          <th colSpan={2} className="put-h">
                            {t("optionsPut")}
                          </th>
                        </tr>
                        <tr>
                          <th>{t("optionsBid")}</th>
                          <th>{t("optionsAsk")}</th>
                          <th className="strike-c">—</th>
                          <th>{t("optionsBid")}</th>
                          <th>{t("optionsAsk")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {optionStrikes.map((strike) => {
                          const { call, put } =
                            optionStrikeMap.get(strike) || {};
                          return (
                            <OptionChainRow
                              key={strike}
                              call={call}
                              put={put}
                              strike={strike}
                              parentSymbol={symbol}
                              exchange={exchangeCode}
                              exchangeStatus={exchange?.status}
                              activeClass="has-p clickable"
                              strikeClass="strike-c"
                              onDeal={goToOptionDeal}
                            />
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
